import { useState, useEffect, useContext } from "react";
import { AttributeData } from "../../utils/types";
import { IngredientInputGroupProps } from "../../utils/props";
import IngredientManager from "../views/IngredientManager";
import { UserContext } from "../../context/UserContext";
import { defaultItem, defaultAmount, defaultUnit, references } from "../../utils/templates";
import FaPlusButton from "../ui/common/FaPlusButton";
import FaMinusButton from "../ui/common/FaMinusButton";
import Alert from "../ui/Alert";
import { DataContext } from "../../context/DataContext";


/** Manages individual components of Ingredient object
 *
 * ComponentsOptionsRequests -> IngredientInputGroup -> IngredientManager
 */
function IngredientInputGroup({
  onIngredientAction,
  ingredient,
  index,
  optionAction,
  length,
}: IngredientInputGroupProps) {
  const [item, setItem] = useState<AttributeData>(ingredient.item);
  const [amount, setAmount] = useState<AttributeData>(ingredient.amount);
  const [unit, setUnit] = useState<AttributeData>(ingredient.unit);
  const [error, setError] = useState<string | null>();
  const [optionsReferences, setOptionsReferences] = useState(references);

  const { userId, currentBookId } = useContext(UserContext);
  const { isBookSource, ingredientOptions } = useContext(DataContext);

  /** Calls parent callback to handleUpdate name */
  function updateIngredientList() {
    const updatedIngredient = { ...ingredient, item, amount, unit };
    onIngredientAction.update(updatedIngredient, index);
  }

  /** Changes selected option */
  function updateSelected(state: string, option: AttributeData) {
    if (state === "item") setItem(option);
    if (state === "unit") setUnit(option);
    if (state === "amount") setAmount(option);
    // Recipe form is rendering global user options - on select option is associated to selected book
    if (isBookSource && isOptionNotAssociated(option, state))
      optionAction.associate(userId, currentBookId, +option.id, state);
  }

  /** Checks if selected user option already exists in list of user's book options */
  function isOptionNotAssociated(
    option: AttributeData,
    state: string,
  ) {
    const isAssociated = optionsReferences[state].some(
      (o) => o.id === option.id,
    );
    return !isAssociated;
  }

  /** Removes selected option */
  function removeSelected(state: string) {
    if (state === "item") setItem(defaultItem);
    if (state === "unit") setUnit(defaultUnit);
    if (state === "amount") setAmount(defaultAmount);
  }

  const handleComponent = {
    updateSelected,
    removeSelected,
    handleError,
  };

  /** Maintains parent components state synced with latest selections */
  useEffect(() => {
    updateIngredientList();
  }, [item, amount, unit]);

  /** Checks if ingredient components have any input. This allows dynamic rendering of
   * "+" or "-" button to add or remove additional ingredient inputs
   */
  function renderHandleIngredientUI() {
    if (length === 0) return <FaPlusButton onAction={onIngredientAction.add} />;
    return (item.id || amount.id || unit.id) && length === index ? (
      <FaPlusButton onAction={onIngredientAction.add} />
    ) : (
      <FaMinusButton onAction={() => onIngredientAction.remove(index)} />
    );
  }

  /** Handle error display */
  function handleError(error: string) {
    setError(error);
    setTimeout(() => {
      setError(null);
    }, 5000);
  }

  useEffect(() => {
    const { items, units, amounts } = ingredientOptions;
    if (isBookSource) {
      setOptionsReferences({ amount: amounts, unit: units, item: items }); ///DOES THIS NEED TO BE MEMOIZED
    }
  }, []);

  return (
    <div>
      <div>{error && <Alert alert={error} degree={"yellow"} />}</div>
      <div className="IngredientInputGroup-Ingredient-section flex rounded-md">
        <IngredientManager
          length={length}
          value={ingredient.amount}
          attribute={"value"}
          entity={"amount"}
          options={ingredientOptions.amounts}
          handleOption={optionAction}
          handleComponent={handleComponent}
          placeholder={"amount (e.g. 2)"}
        />
        <IngredientManager
          length={length}
          value={ingredient.unit}
          attribute={"type"}
          entity={"unit"}
          options={ingredientOptions.units}
          handleOption={optionAction}
          handleComponent={handleComponent}
          placeholder={"unit: oz"}
        />
        <IngredientManager
          length={length}
          value={ingredient.item}
          attribute={"name"}
          entity={"item"}
          options={ingredientOptions.items}
          handleOption={optionAction}
          handleComponent={handleComponent}
          placeholder={"item (gin)"}
        />
        {renderHandleIngredientUI()}
      </div>
    </div>
  );
}

export default IngredientInputGroup;
